import { useCallback } from 'react';
import { useNavigate, useLocation, NavigateOptions } from 'react-router-dom';

/**
 * Helper function to build locale-aware paths
 * Extracts locale from current path and prepends it to the new path
 */
const buildLocalePath = (currentPath: string, newPath: string): string => {
  // Don't modify if it's already a full URL or has a locale
  if (newPath.startsWith('http') || newPath.match(/^\/[a-z]{2}-[a-z]{2}\//i)) {
    return newPath;
  }

  const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})\//i);
  if (localeMatch) {
    const locale = localeMatch[1];
    return `/${locale}${newPath}`;
  }
  return newPath;
};

/**
 * Custom hook that wraps useNavigate to automatically handle locale prefixes
 * @returns A navigate function that preserves locale context
 * @example
 * const navigate = useLocaleNavigate();
 * navigate('/prospect-desk/prospects'); // Automatically becomes /en-ca/prospect-desk/prospects if on en-ca locale
 */
export const useLocaleNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const localeNavigate = useCallback(
    (to: string | number, options?: NavigateOptions) => {
      // Handle numeric navigation (back/forward)
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      // Build locale-aware path
      const localePath = buildLocalePath(location.pathname, to);
      navigate(localePath, options);
    },
    [navigate, location.pathname]
  );

  return localeNavigate;
};
